import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { BsBoxSeam, BsCheckCircle, BsTruck, BsGeoAlt } from 'react-icons/bs'
import { FiClock, FiPhone, FiUser, FiPackage } from 'react-icons/fi'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import axios from 'axios'
import Container from './styles'

// Fix Leaflet default marker icons
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

// Custom marker icons
const pickupIcon = new L.DivIcon({
  html: `<div style="background: #4CAF50; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <span style="color: white; font-size: 18px;">📦</span>
  </div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
})

const deliveryIcon = new L.DivIcon({
  html: `<div style="background: #FE7D11; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <span style="color: white; font-size: 18px;">🎯</span>
  </div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
})

const riderIcon = new L.DivIcon({
  html: `<div style="background: #2196F3; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 4px 12px rgba(33, 150, 243, 0.5); animation: pulse 2s infinite;">
    <span style="color: white; font-size: 20px;">🏍️</span>
  </div>`,
  className: '',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
})

// Component to fit map bounds
const FitBounds = ({ bounds }) => {
  const map = useMap()
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] })
    }
  }, [bounds, map])
  
  return null
}

const TrackOrder = () => {
  const { trackingNumber } = useParams()
  const navigate = useNavigate()
  const [orderData, setOrderData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchInput, setSearchInput] = useState(trackingNumber || '')
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const [routeCoordinates, setRouteCoordinates] = useState([])
  const [routeInfo, setRouteInfo] = useState(null) // Distance, duration, steps

  useEffect(() => {
    if (trackingNumber) {
      fetchOrderTracking(trackingNumber)
    } else {
      setLoading(false)
    }
  }, [trackingNumber])

  // Auto-refresh rider location every 15 seconds if tracking an active order
  useEffect(() => {
    if (!orderData || !trackingNumber) return

    const refreshInterval = setInterval(() => {
      fetchOrderTracking(trackingNumber, true) // silent refresh
    }, 15000)

    return () => clearInterval(refreshInterval)
  }, [orderData, trackingNumber])

  const fetchOrderTracking = async (trackingNum, silent = false) => {
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    
    try {
      const response = await axios.post(
        `https://api.astar.ng/api.php?path=/api/v1/admin/order_details`,
        {
          trackingNumber: trackingNum
        }
      )

      if (response.data.status === 200 && response.data.data) {
        const order = response.data.data.order || response.data.data
        setOrderData(order)
        setLastUpdate(new Date())
        if (!silent) {
          setLoading(false)
        }
      } else {
        if (!silent) {
          setError(response.data.message || 'Order not found')
          setLoading(false)
        }
      }
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.message || 'Failed to fetch order tracking')
        setLoading(false)
      }
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchInput.trim()) {
      navigate(`/track-order/${searchInput.trim()}`)
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return <FiClock />
      case 'ACCEPTED':
        return <BsCheckCircle />
      case 'PICKED_UP':
        return <BsBoxSeam />
      case 'IN_TRANSIT':
        return <BsTruck />
      case 'COMPLETED':
      case 'DELIVERED':
        return <BsCheckCircle />
      default:
        return <FiPackage />
    }
  }

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return '#FFA500'
      case 'ACCEPTED':
        return '#4A90E2'
      case 'PICKED_UP':
        return '#9B59B6'
      case 'IN_TRANSIT':
        return '#FE7D11'
      case 'COMPLETED':
      case 'DELIVERED':
        return '#27AE60'
      default:
        return '#95A5A6'
    }
  }

  const getStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'Order Received'
      case 'ACCEPTED':
        return 'Rider Assigned'
      case 'PICKED_UP':
        return 'Picked Up'
      case 'IN_TRANSIT':
        return 'In Transit'
      case 'COMPLETED':
      case 'DELIVERED':
        return 'Delivered'
      default:
        return status
    }
  }

  const formatDistance = (meters) => {
    if (meters < 1000) {
      return `${Math.round(meters)} m`
    }
    return `${(meters / 1000).toFixed(1)} km`
  }

  const formatDuration = (seconds) => {
    const minutes = Math.round(seconds / 60)
    if (minutes < 60) {
      return `${minutes} min`
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}h ${mins}m`
  }

  const getManeuverIcon = (type) => {
    const icons = {
      'turn': '↻',
      'new name': '➡️',
      'depart': '🚀',
      'arrive': '🎯',
      'merge': '↗️',
      'on ramp': '⤴️',
      'off ramp': '⤵️',
      'fork': '⑂',
      'end of road': '⊣',
      'continue': '↑',
      'roundabout': '⟲',
      'rotary': '⟲',
      'roundabout turn': '⟲',
      'notification': 'ℹ️',
      'exit roundabout': '↱',
      'exit rotary': '↱'
    }
    
    for (const [key, icon] of Object.entries(icons)) {
      if (type?.includes(key)) return icon
    }
    return '➡️'
  }

  const getMapBounds = () => {
    if (!orderData) return []

    const bounds = [
      [orderData.pickupLat, orderData.pickupLng],
      [orderData.deliveryLat, orderData.deliveryLng],
    ]
    
    if (orderData.riderLat && orderData.riderLng) {
      bounds.push([orderData.riderLat, orderData.riderLng])
    }

    return bounds
  }

  const decodePolyline = (encoded) => {
    const points = []
    let index = 0
    const len = encoded.length
    let lat = 0
    let lng = 0

    while (index < len) {
      let b
      let shift = 0
      let result = 0
      do {
        b = encoded.charCodeAt(index++) - 63
        result |= (b & 0x1f) << shift
        shift += 5
      } while (b >= 0x20)
      const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1
      lat += dlat

      shift = 0
      result = 0
      do {
        b = encoded.charCodeAt(index++) - 63
        result |= (b & 0x1f) << shift
        shift += 5
      } while (b >= 0x20)
      const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1
      lng += dlng

      points.push([lat / 1e5, lng / 1e5])
    }

    return points
  }

  const fetchRoute = async (start, end) => {
    try {
      const response = await axios.get(
        `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${end[1]},${end[0]}?overview=full&geometries=polyline&steps=true`
      )

      if (response.data.code === 'Ok' && response.data.routes && response.data.routes.length > 0) {
        const route = response.data.routes[0]
        const coordinates = decodePolyline(route.geometry)
        
        // Extract route information
        const info = {
          distance: route.distance, // meters
          duration: route.duration, // seconds
          steps: route.legs[0]?.steps || []
        }
        
        return { coordinates, info }
      }
    } catch (error) {
      console.error('Error fetching route:', error)
    }
    
    // Fallback to straight line
    return { 
      coordinates: [start, end],
      info: null
    }
  }

  const updateRouteCoordinates = async () => {
    if (!orderData) return

    try {
      const pickup = [orderData.pickupLat, orderData.pickupLng]
      const delivery = [orderData.deliveryLat, orderData.deliveryLng]
      const rider = orderData.riderLat && orderData.riderLng 
        ? [orderData.riderLat, orderData.riderLng] 
        : null

      let routeData = null

      if (rider) {
        // Fetch route from rider to delivery
        routeData = await fetchRoute(rider, delivery)
      } else {
        // Fetch route from pickup to delivery
        routeData = await fetchRoute(pickup, delivery)
      }

      setRouteCoordinates(routeData.coordinates)
      setRouteInfo(routeData.info)
    } catch (error) {
      console.error('Error updating route:', error)
      // Fallback to straight line
      setRouteCoordinates(getRoutePoints())
      setRouteInfo(null)
    }
  }

  useEffect(() => {
    if (orderData) {
      updateRouteCoordinates()
    }
  }, [orderData])

  if (loading) {
    return (
      <Container>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading order details...</p>
        </div>
      </Container>
    )
  }

  return (
    <Container>
      <div className="track-header">
        <h1>Track Your Order</h1>
        <p>Enter your tracking number to see real-time delivery status</p>
        
        <form className="search-form" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Enter tracking number (e.g., PKP...)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">
            Track Order
          </button>
        </form>
      </div>

      {error && (
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h3>Order Not Found</h3>
          <p>{error}</p>
          <button onClick={() => setSearchInput('')} className="retry-btn">
            Try Another Tracking Number
          </button>
        </div>
      )}

      {orderData && !error && (
        <div className="tracking-content">
          <div className="tracking-grid">
            {/* Left Column - Order Details */}
            <div className="details-section">
              <div className="status-card">
                <div className="status-header" style={{ borderColor: getStatusColor(orderData.status) }}>
                  <div className="status-icon" style={{ color: getStatusColor(orderData.status) }}>
                    {getStatusIcon(orderData.status)}
                  </div>
                  <div className="status-info">
                    <span className="status-label">Current Status</span>
                    <h2 style={{ color: getStatusColor(orderData.status) }}>
                      {getStatusLabel(orderData.status)}
                    </h2>
                  </div>
                </div>

                <div className="order-info">
                  <div className="info-row">
                    <span className="info-label">Tracking Number</span>
                    <span className="info-value">{orderData.trackingNumber}</span>
                  </div>
                  {orderData.estimatedDelivery && (
                    <div className="info-row">
                      <span className="info-label">Estimated Delivery</span>
                      <span className="info-value">{new Date(orderData.estimatedDelivery).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Rider Info */}
              {(orderData.riderName || orderData.riderId) && (
                <div className="rider-card">
                  <h3>
                    <FiUser /> Rider Information
                  </h3>
                  <div className="rider-details">
                    <div className="rider-name">
                      <strong>{orderData.riderName || 'Rider Assigned'}</strong>
                      {orderData.riderPhone && (
                        <a href={`tel:${orderData.riderPhone}`} className="phone-link">
                          <FiPhone /> {orderData.riderPhone}
                        </a>
                      )}
                    </div>
                    {orderData.riderLat && orderData.riderLng && orderData.deliveryLat && orderData.deliveryLng && (
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&origin=${orderData.riderLat},${orderData.riderLng}&destination=${orderData.deliveryLat},${orderData.deliveryLng}&travelmode=driving`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="directions-btn"
                      >
                        🧭 Get Directions to Delivery
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Location Details */}
              <div className="location-card">
                <h3>
                  <BsGeoAlt /> Delivery Details
                </h3>
                <div className="location-item">
                  <div className="location-icon pickup">
                    <BsGeoAlt />
                  </div>
                  <div className="location-info">
                    <span className="location-label">Pickup</span>
                    <p>{orderData.pickupAddress}</p>
                    {orderData.pickupInstructions && (
                      <span className="contact-name">{orderData.pickupInstructions}</span>
                    )}
                  </div>
                </div>
                <div className="location-divider"></div>
                <div className="location-item">
                  <div className="location-icon delivery">
                    <BsGeoAlt />
                  </div>
                  <div className="location-info">
                    <span className="location-label">Delivery</span>
                    <p>{orderData.deliveryAddress}</p>
                    {orderData.customerName && (
                      <span className="contact-name">{orderData.customerName}</span>
                    )}
                    {orderData.customerPhone && (
                      <span className="contact-phone">{orderData.customerPhone}</span>
                    )}
                    {orderData.deliveryInstructions && (
                      <span className="delivery-notes">{orderData.deliveryInstructions}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Package Info */}
              {orderData.packageDescription && (
                <div className="package-card">
                  <h3>
                    <FiPackage /> Package Information
                  </h3>
                  <p>{orderData.packageDescription}</p>
                  <div className="package-details">
                    {orderData.packageWeight && (
                      <span>Weight: {orderData.packageWeight} kg</span>
                    )}
                    {orderData.packageSize && (
                      <span>Size: {orderData.packageSize}</span>
                    )}
                    {orderData.deliveryFee && (
                      <span>Delivery Fee: ₦{orderData.deliveryFee.toLocaleString()}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Route Directions */}
              {routeInfo && routeInfo.steps && routeInfo.steps.length > 0 && (
                <div className="directions-card">
                  <h3>
                    <BsTruck /> Route Directions
                  </h3>
                  
                  {/* Route Summary */}
                  <div className="route-summary">
                    <div className="route-stat">
                      <span className="stat-icon">📏</span>
                      <div>
                        <span className="stat-label">Distance</span>
                        <span className="stat-value">{formatDistance(routeInfo.distance)}</span>
                      </div>
                    </div>
                    <div className="route-stat">
                      <span className="stat-icon">⏱️</span>
                      <div>
                        <span className="stat-label">ETA</span>
                        <span className="stat-value">{formatDuration(routeInfo.duration)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Turn-by-Turn Directions */}
                  <div className="directions-list">
                    {routeInfo.steps.slice(0, 8).map((step, index) => (
                      <div key={index} className="direction-step">
                        <div className="step-icon">{getManeuverIcon(step.maneuver?.type)}</div>
                        <div className="step-content">
                          <div className="step-instruction">
                            {step.maneuver?.modifier && (
                              <strong>{step.maneuver.modifier.replace('_', ' ')} </strong>
                            )}
                            {step.name || 'Continue'}
                          </div>
                          <div className="step-distance">{formatDistance(step.distance)}</div>
                        </div>
                      </div>
                    ))}
                    {routeInfo.steps.length > 8 && (
                      <div className="more-steps">
                        +{routeInfo.steps.length - 8} more steps
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Map */}
            <div className="map-section">
              <div className="map-container">
                <MapContainer
                  center={[orderData.pickupLat, orderData.pickupLng]}
                  zoom={13}
                  style={{ width: '100%', height: '100%' }}
                  zoomControl={true}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  {/* Pickup Marker */}
                  <Marker 
                    position={[orderData.pickupLat, orderData.pickupLng]}
                    icon={pickupIcon}
                  >
                    <Popup>
                      <div style={{ padding: '8px' }}>
                        <strong>🟢 Pickup Location</strong>
                        <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                          {orderData.pickupAddress}
                        </p>
                      </div>
                    </Popup>
                  </Marker>

                  {/* Delivery Marker */}
                  <Marker 
                    position={[orderData.deliveryLat, orderData.deliveryLng]}
                    icon={deliveryIcon}
                  >
                    <Popup>
                      <div style={{ padding: '8px' }}>
                        <strong>🔴 Delivery Location</strong>
                        <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                          {orderData.deliveryAddress}
                        </p>
                        {orderData.customerName && (
                          <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                            <strong>Customer:</strong> {orderData.customerName}
                          </p>
                        )}
                      </div>
                    </Popup>
                  </Marker>

                  {/* Rider Marker */}
                  {orderData.riderLat && orderData.riderLng && (
                    <Marker 
                      position={[orderData.riderLat, orderData.riderLng]}
                      icon={riderIcon}
                    >
                      <Popup>
                        <div style={{ padding: '8px' }}>
                          <strong>🚴 Rider Location</strong>
                          <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                            <strong>{orderData.riderName || 'Rider'}</strong>
                          </p>
                          {orderData.riderPhone && (
                            <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                              📞 {orderData.riderPhone}
                            </p>
                          )}
                          <div style={{ 
                            marginTop: '8px', 
                            padding: '4px 8px', 
                            background: '#4CAF50', 
                            color: 'white', 
                            borderRadius: '4px', 
                            fontSize: '11px',
                            fontWeight: 'bold',
                            display: 'inline-block'
                          }}>
                            LIVE
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  )}

                  {/* Route Polyline - Shows actual road route */}
                  {routeCoordinates.length > 0 && (
                    <Polyline 
                      positions={routeCoordinates}
                      color="#4A90E2"
                      weight={5}
                      opacity={0.8}
                    />
                  )}

                  {/* Fit bounds to show all markers */}
                  <FitBounds bounds={getMapBounds()} />
                </MapContainer>
              </div>
              <div className="map-legend">
                <div className="legend-item">
                  <span className="legend-marker pickup"></span>
                  <span>Pickup Location</span>
                </div>
                <div className="legend-item">
                  <span className="legend-marker delivery"></span>
                  <span>Delivery Location</span>
                </div>
                {orderData.riderLat && orderData.riderLng && (
                  <div className="legend-item">
                    <span className="legend-marker rider"></span>
                    <span>Rider Location (Live)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  )
}

export default TrackOrder
